import { computed, ref, type Ref } from 'vue';
import type { TableColumnsType, TableColumnType } from 'ant-design-vue';

const MIN_COL_WIDTH = 60;
/** 多选列大约宽度，计入 scroll.x */
const SELECTION_COL_WIDTH = 48;

function sumColumnWidths(cols: TableColumnsType): number {
  return cols.reduce((sum, col) => sum + (Number(col.width) || 0), 0);
}

export interface UseResizableColumnsOptions {
  /** 是否有多选列；无勾选时不要计入 scroll.x，否则固定列表头易错位 */
  hasSelection?: boolean;
}

/**
 * 使用 ant-design-vue 内置列宽拖拽（column.resizable + @resizeColumn）
 * 拖拽条在列与列之间的空隙，与设计器表格一致
 */
export function useResizableColumns(
  baseColumns: TableColumnsType,
  options?: UseResizableColumnsOptions,
): {
  columns: Ref<TableColumnsType>;
  scrollX: Ref<number>;
  handleResizeColumn: (w: number, col: TableColumnType) => void;
} {
  const hasSelection = options?.hasSelection !== false;
  const columns = ref<TableColumnsType>(
    baseColumns.map((col) => ({
      ...col,
      // 允许列自行关闭拖拽（如操作列），避免表头拖拽条造成视觉错位
      resizable: (col as TableColumnType & { resizable?: boolean }).resizable !== false,
      width: Number(col.width) || MIN_COL_WIDTH,
      minWidth: MIN_COL_WIDTH,
    })),
  );

  const scrollX = computed(
    () => sumColumnWidths(columns.value) + (hasSelection ? SELECTION_COL_WIDTH : 0),
  );

  function handleResizeColumn(w: number, col: TableColumnType) {
    col.width = Math.max(MIN_COL_WIDTH, Math.round(w));
  }

  return {
    columns,
    scrollX,
    handleResizeColumn,
  };
}
